// ─────────────────────────────────────────────
// KLOTHIQ — App Layout (Collapsible Sidebar with Hover Expand)
// ─────────────────────────────────────────────
// Expanded  : fixed sidebar w-56, shows icon + label
// Collapsed : fixed sidebar w-14, hover → expands to w-56 + shows labels
// Mobile    : full-width drawer overlay

import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { permissionForPath, roleLabel } from '@klothiq/domain';
import BrandLogo from '@/components/common/BrandLogo';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Truck,
  ClipboardList, BarChart3, History, Settings,
  LogOut, Menu, X, Warehouse, Trash2, Sliders,
  ChevronRight, ChevronLeft, Sun, Moon, Landmark,
  CheckSquare, ListTodo, Printer,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import PWAInstallPrompt from '@/components/common/PWAInstallPrompt';
import MutationLoadingOverlay from '@/components/common/MutationLoadingOverlay';
import NotificationPopover from '@/components/notifications/NotificationPopover';
import UserGuide from '@/components/help/UserGuide';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Users & Access', path: '/settings/users', icon: <Users className="h-[18px] w-[18px] shrink-0" />, roles: ['owner'] },
  { label: 'Dashboard',       path: '/',                icon: <LayoutDashboard className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Products',        path: '/products',        icon: <Package          className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Inventory',       path: '/inventory',       icon: <Warehouse        className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Suppliers',       path: '/suppliers',       icon: <Truck            className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Purchases',       path: '/purchases',       icon: <ClipboardList    className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Customers',       path: '/customers',       icon: <Users            className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Sales',           path: '/sales',           icon: <ShoppingCart     className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Print templates', path: '/print-templates', icon: <Printer className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Delivery', path: '/deliveries', icon: <Truck className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Accounts',        path: '/accounts',        icon: <Landmark         className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Plans & Tasks',   path: '/plans',           icon: <CheckSquare     className="h-[18px] w-[18px] shrink-0" />, roles: ['owner', 'admin'] },
  { label: 'My Tasks',        path: '/my-tasks',        icon: <ListTodo        className="h-[18px] w-[18px] shrink-0" /> },
  {label:'My Sales',path:'/my-sales',icon:<ShoppingCart className="h-[18px] w-[18px] shrink-0"/>},
  {label:'Team Sales',path:'/team-sales',icon:<BarChart3 className="h-[18px] w-[18px] shrink-0"/>},
  { label: 'Reports',         path: '/reports',         icon: <BarChart3        className="h-[18px] w-[18px] shrink-0" /> },
  { label: 'Master Config',   path: '/master-config',   icon: <Sliders          className="h-[18px] w-[18px] shrink-0" />, roles: ['owner', 'admin'] },
  { label: 'Audit History',   path: '/audit',           icon: <History          className="h-[18px] w-[18px] shrink-0" />, roles: ['owner', 'admin'] },
  { label: 'Recycle Bin',     path: '/recycle-bin',     icon: <Trash2           className="h-[18px] w-[18px] shrink-0" />, roles: ['owner', 'admin'] },
  { label: 'Data Migration',  path: '/settings/import', icon: <Settings         className="h-[18px] w-[18px] shrink-0" />, roles: ['owner', 'admin'] },
  { label: 'My Account', path: '/my-account', icon: <Settings className="h-[18px] w-[18px] shrink-0" /> },
];

// ── Breadcrumb ───────────────────────────────
function getBreadcrumb(pathname: string, navItems: NavItem[]) {
  const sorted = [...navItems].sort((a, b) => b.path.length - a.path.length);
  const match = sorted.find(
    (item) => pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path)),
  );
  if (!match) return { label: 'KLOTHIQ' };
  const sub = pathname.replace(match.path, '').replace(/^\//, '');
  if (sub === 'new') return { label: 'New', parent: match.label };
  if (sub.includes('edit')) return { label: 'Edit', parent: match.label };
  return { label: match.label };
}

// ── Theme Toggle ─────────────────────────────
function useTheme() {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const saved = localStorage.getItem('klothiq-theme');
    if (saved === 'light') {
      document.documentElement.classList.remove('dark');
      return false;
    }
    document.documentElement.classList.add('dark');
    return true;
  });
  const toggle = () => {
    const next = !isDark;
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('klothiq-theme', next ? 'dark' : 'light');
    setIsDark(next);
  };
  return { isDark, toggle };
}

export default function AppLayout() {
  const { profile, signOut, role, can } = useAuth();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('klothiq-sidebar') === 'collapsed';
  });
  const { isDark, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const productEditor = /^\/products\/(?:new|[^/]+\/edit)\/?$/.test(location.pathname);

  const filteredNav = NAV_ITEMS.filter(
    (item) => (!item.roles || (role && item.roles.includes(role))) && (!permissionForPath(item.path) || can(permissionForPath(item.path)!)),
  );
  const breadcrumb = getBreadcrumb(location.pathname, filteredNav);
  const initials = profile?.full_name?.charAt(0)?.toUpperCase() ?? 'U';

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('klothiq-sidebar', next ? 'collapsed' : 'expanded');
      return next;
    });
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch (err) { console.error('Sign out failed:', err); }
  };

  return (
    <div className="app-shell flex h-full min-h-0 overflow-hidden bg-background">

      {/* ── Mobile overlay ── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true" data-field-guide-scope
        />
      )}

      {/* ═══════════════════════════════════════════════════
          DESKTOP SIDEBAR STRUCTURE
          Spacer div  → reserves layout space (never changes during hover)
          Fixed aside → the actual sidebar panel (overlays on hover expand)
      ═══════════════════════════════════════════════════ */}

      {/* Layout spacer — keeps main content from shifting on hover */}
      <div
        className={cn(
          'hidden lg:block shrink-0 transition-[width] duration-300',
          isCollapsed ? 'w-14' : 'w-56',
        )}
        aria-hidden="true"
      />

      {/* ── Fixed sidebar panel ── */}
      <aside
        className={cn(
          // Fixed positioning so it can overlay content when hover-expanding
          'fixed inset-y-0 left-0 z-30',
          'hidden lg:flex flex-col',
          'border-r border-sidebar-border bg-sidebar',
          // group/sidebar: enables group-hover/sidebar: selectors inside
          'group/sidebar overflow-hidden',
          // Width: w-56 when expanded, w-14 when collapsed
          // When collapsed: hover expands to w-56 and shows a stronger shadow overlay
          isCollapsed
            ? 'w-14 hover:w-56 hover:shadow-[4px_0_24px_rgba(0,0,0,0.12)] dark:hover:shadow-[4px_0_24px_rgba(0,0,0,0.5)] transition-[width,box-shadow] duration-200 ease-out'
            : 'w-56 transition-[width] duration-300 ease-in-out',
        )}
      >
        {/* ── Brand header ── */}
        <div className="flex h-14 shrink-0 items-center border-b border-sidebar-border px-3 gap-2.5">
          {/* Logo — always visible */}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10 border border-primary/20">
            <BrandLogo className="h-full w-full" width={32} height={32} />
          </div>

          {/* Brand text — hidden when collapsed, shown on hover/expand */}
          <div className={cn(
            'min-w-0 flex-1 overflow-hidden transition-[max-width,opacity] duration-200',
            isCollapsed
              ? 'max-w-0 opacity-0 group-hover/sidebar:max-w-[10rem] group-hover/sidebar:opacity-100'
              : 'max-w-[10rem] opacity-100',
          )}>
            <p className="text-sm font-bold tracking-widest text-foreground whitespace-nowrap leading-none">KLOTHIQ</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-primary/60 mt-0.5 whitespace-nowrap">Admin</p>
          </div>

          {/* Collapse / Expand button */}
          <button
            onClick={toggleCollapsed}
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
              'text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors',
              // When collapsed: hide until hover (don't clutter icon-only mode)
              isCollapsed ? 'opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-150' : '',
            )}
            title={isCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav
          className="flex-1 overflow-y-auto px-2 py-3"
          aria-label="Main navigation"
        >
          <ul className="space-y-0.5">
            {filteredNav.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-0 rounded-lg px-2.5 py-2 text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'nav-active font-semibold'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                    )
                  }
                >
                  {/* Icon — always visible */}
                  {item.icon}

                  {/* Label — hidden when collapsed, slides in on hover/expand */}
                  <span
                    className={cn(
                      'overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-200',
                      isCollapsed
                        ? 'max-w-0 opacity-0 ml-0 group-hover/sidebar:max-w-[10rem] group-hover/sidebar:opacity-100 group-hover/sidebar:ml-3'
                        : 'max-w-[10rem] opacity-100 ml-3',
                    )}
                  >
                    {item.label}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* ── User section ── */}
        <div className="border-t border-sidebar-border p-3">
          <div className="flex items-center gap-0">
            {/* Avatar — always visible */}
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary ring-1 ring-primary/30 cursor-default"
              title={profile?.full_name ?? ''}
            >
              {initials}
            </div>

            {/* User info + sign out — hidden when collapsed, shown on hover/expand */}
            <div
              className={cn(
                'flex items-center overflow-hidden transition-[max-width,opacity,margin] duration-200',
                isCollapsed
                  ? 'max-w-0 opacity-0 ml-0 group-hover/sidebar:max-w-[12rem] group-hover/sidebar:opacity-100 group-hover/sidebar:ml-2.5'
                  : 'max-w-[12rem] opacity-100 ml-2.5',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-sidebar-accent-foreground whitespace-nowrap">
                  {profile?.full_name ?? 'User'}
                </p>
                <p className="truncate text-[10px] capitalize text-sidebar-foreground whitespace-nowrap">
                  {roleLabel(role)}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sidebar-foreground hover:bg-destructive/15 hover:text-destructive transition-colors"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="h-[15px] w-[15px]" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ═══════════════════════════════════════════════════
          MOBILE SIDEBAR — Full-width drawer
      ═══════════════════════════════════════════════════ */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col',
          'border-r border-sidebar-border bg-sidebar',
          'transition-transform duration-300 lg:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 overflow-hidden rounded-lg bg-primary/10 border border-primary/20">
              <BrandLogo className="h-full w-full" width={32} height={32} />
            </div>
            <div>
              <p className="text-sm font-bold tracking-widest text-foreground">KLOTHIQ</p>
              <p className="text-[10px] text-primary/60 uppercase tracking-widest">Admin</p>
            </div>
          </div>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="rounded-lg p-1.5 text-sidebar-foreground hover:bg-sidebar-accent"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Mobile navigation">
          <ul className="space-y-0.5">
            {filteredNav.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setMobileSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'nav-active font-semibold'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                    )
                  }
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-sidebar-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary ring-1 ring-primary/30">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-sidebar-accent-foreground">{profile?.full_name ?? 'User'}</p>
              <p className="truncate text-xs capitalize text-sidebar-foreground">{role ?? '...'}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="rounded-lg p-1.5 text-sidebar-foreground hover:bg-destructive/15 hover:text-destructive transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ═══════════════════════════════════════════════════
          MAIN CONTENT AREA
      ═══════════════════════════════════════════════════ */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden min-w-0">

        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card/60 backdrop-blur-md px-4 lg:px-5">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex flex-1 items-center gap-1.5 min-w-0">
            {breadcrumb.parent ? (
              <>
                <span className="text-sm text-muted-foreground truncate">{breadcrumb.parent}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                <span className="text-sm font-semibold text-foreground truncate">{breadcrumb.label}</span>
              </>
            ) : (
              <span className="text-sm font-semibold text-foreground">{breadcrumb.label}</span>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <UserGuide compact />
            <NotificationPopover />

            <button
              onClick={toggleTheme}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              aria-label={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary ring-1 ring-primary/30 cursor-default select-none"
              title={profile?.full_name ?? ''}
            >
              {initials}
            </div>
          </div>
        </header>

        <main className={cn('min-h-0 flex-1', productEditor ? 'flex flex-col overflow-hidden' : 'overflow-y-auto')}>
          <div className={cn('p-4 lg:p-5', productEditor ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'page-enter')}>
            <Outlet />
          </div>
        </main>

        <PWAInstallPrompt />
        <MutationLoadingOverlay />
      </div>
    </div>
  );
}
